import React, { ElementType, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shelf } from '@src/remotes/Shelf';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import { useRecommendations } from 'src/components/Providers/RecommendationsProvider';
import { QuizCallout } from '@src/components/QuizCallout';
import { hasQuizAnswers } from 'src/utilities/userUtility';
import { NoQuizResults } from 'src/components/NoQuizResults';
import { useBookmarkActions } from '@src/hooks';
import { ShimmerLoader, Typography } from '@guildeducationinc/recess';
import { PROGRAM_RECS } from '@src/constants/featureFlags';
import { useDecision } from '@optimizely/react-sdk';
import classNames from 'classnames';
import { RecommendationCategory } from '@src/graphqlTypes/catalogServiceTypes';
import { getUserValuesForPath } from '@src/constants/getUserValuesForPath';
import { useBookmarks } from '../Providers/BookmarksProvider';
import { QuizShelfHeading } from '../QuizShelfHeading';
import { ProductUsableComponents, useProductUsable } from '../Providers/ProductUsableProvider';
import { getAnswerTags } from './helpers';
import styles from './QuizShelf.module.scss';

export const PERSONALIZED_SHELF_HEADING = "Don't see what you're looking for?";
export const PERSONALIZED_SHELF_SUBHEADING =
	'Check out some other programs we think you might like.';
export const PERSONALIZED_SHELF_HEADING_NO_QUIZ_RESULTS =
	'Or check out some programs we think you might like.';

export const QuizShelf: React.FC = () => {
	const { bookmarks } = useBookmarks();
	const { updateBookmark } = useBookmarkActions();
	const { getAccessToken, getIdToken, userFromIdToken: user } = useMemberExperienceAuth();
	const authUser = getUserValuesForPath(user);

	const { recommendationsData, recommendationsLoading } = useRecommendations();
	const [programRecsDecision] = useDecision(PROGRAM_RECS);
	const { componentLoaded } = useProductUsable();

	const layout = programRecsDecision.variables?.layout;

	const quizResponses = recommendationsData?.recommendationQuizResponses;
	const quizRecs = recommendationsData?.recommendations || [];
	const hasAnswers =
		hasQuizAnswers(recommendationsData) && recommendationsData.hasQuizAnswersFromCatalogSource;
	const hasNoHits = quizRecs.length === 0;
	const limitedRecommendationsUuids = quizRecs.slice(0, 9).map(rec => rec.uuid);

	const personalizedShelfHeading = () => {
		if (!hasAnswers || hasNoHits) {
			return (
				<Typography className={styles.headingWithDivider} variant='body2'>
					<span>{PERSONALIZED_SHELF_HEADING_NO_QUIZ_RESULTS}</span>
				</Typography>
			);
		}
		return (
			<>
				<Typography
					Component='h3'
					className={classNames([styles.headingWithDivider, styles.heading])}
					variant='title3'
				>
					<span>{PERSONALIZED_SHELF_HEADING}</span>
				</Typography>
				<Typography variant='body2'>{PERSONALIZED_SHELF_SUBHEADING}</Typography>
			</>
		);
	};

	function renderPersonalizedShelf() {
		return (
			<div className={styles.personalizedShelf} data-testid='personalized-shelf'>
				{personalizedShelfHeading()}
				<Shelf
					LinkComponent={Link as unknown as ElementType}
					bookmarks={bookmarks}
					getAccessToken={getAccessToken}
					getIdToken={getIdToken}
					onLoaded={() => componentLoaded(ProductUsableComponents.PERSONALIZED_SHELF)}
					product='catalog'
					recommendationCategory={RecommendationCategory.PERSONALIZED}
					shelfType='personalized_shelf'
					subhead=' '
					updateBookmark={updateBookmark}
					user={authUser}
				/>
			</div>
		);
	}

	useEffect(() => {
		if (hasNoHits) {
			componentLoaded(ProductUsableComponents.QUIZ_SHELF);
		}
	}, [componentLoaded, hasNoHits]);

	if (recommendationsLoading)
		return <ShimmerLoader height={350} label='Quiz Results Loading' loaded={false} />;

	if (hasAnswers && hasNoHits)
		return (
			<>
				<NoQuizResults />
				{layout === 'one_tab' && renderPersonalizedShelf()}
			</>
		);

	if (!hasAnswers && hasNoHits)
		return (
			<>
				<QuizCallout />
				{layout === 'one_tab' && renderPersonalizedShelf()}
			</>
		);

	return (
		<>
			<div className={styles.quizShelf} data-testid='quiz-results'>
				<Shelf
					LinkComponent={Link as unknown as ElementType}
					bookmarks={bookmarks}
					getAccessToken={getAccessToken}
					getIdToken={getIdToken}
					onLoaded={() => componentLoaded(ProductUsableComponents.QUIZ_SHELF)}
					product='catalog'
					programUuids={limitedRecommendationsUuids}
					quizTags={getAnswerTags(quizResponses)}
					shelfType='quiz_shelf'
					subhead={<QuizShelfHeading />}
					updateBookmark={updateBookmark}
					user={authUser}
				/>
			</div>

			{layout === 'one_tab' && renderPersonalizedShelf()}
		</>
	);
};
